import { t as tr, LOCALES } from '../i18n.js'
import { useState, type FormEvent } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { CopyButton } from '../copy.js'
import { RowMenu } from '../menu.js'
import { api, ApiError, browserTimezone, type ApiToken, type DirectoryUser, type Invite, type Role } from '../api.js'
import { atLeast, useAuth } from '../auth.js'
import { fmt } from './Account.js'
import { IpAllowlistEditor } from './IpAllowlist.js'
import { CostRatesEditor } from './CostRates.js'
import { COMMON_ZONES } from './Projects.js'

const ROLES: Role[] = ['admin', 'manager', 'member', 'viewer']

export function PeoplePage() {
  const { user } = useAuth()
  const isAdmin = atLeast(user, 'admin')
  const qc = useQueryClient()
  const [showDeactivated, setShowDeactivated] = useState(false)
  const users = useQuery({
    queryKey: ['users', showDeactivated],
    queryFn: () => api<{ users: DirectoryUser[] }>(`/users?includeDeactivated=${showDeactivated}`),
  })
  const patch = useMutation({
    mutationFn: ({ id, body }: { id: string; body: Record<string, unknown> }) => api(`/users/${id}`, { method: 'PATCH', json: body }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['users'] }),
  })
  const all = users.data?.users ?? []
  const byId = new Map(all.map((u) => [u.id, u]))
  const [showServiceAccounts, setShowServiceAccounts] = useState(false)
  // Robots outnumber people quickly; keep the human roster readable by default.
  const visible = showServiceAccounts ? all : all.filter((u) => !u.serviceAccount)
  const [ipUser, setIpUser] = useState<DirectoryUser | null>(null)
  const [rateUser, setRateUser] = useState<DirectoryUser | null>(null)
  const [editUser, setEditUser] = useState<DirectoryUser | null>(null)
  const [tokenUser, setTokenUser] = useState<DirectoryUser | null>(null)
  const [resetInfo, setResetInfo] = useState<{ email: string; url: string; expiresAt: string } | null>(null)
  const unlock = useMutation({ mutationFn: (id: string) => api<{ cleared: number }>(`/users/${id}/unlock`, { method: 'POST' }), onSuccess: (r) => alert(`Unlocked (${r.cleared} failed attempts cleared).`) })
  const resetLink = useMutation({ mutationFn: (u: DirectoryUser) => api<{ resetUrl: string; expiresAt: string }>(`/users/${u.id}/reset-link`, { method: 'POST' }).then((r) => ({ email: u.email, url: r.resetUrl, expiresAt: r.expiresAt })), onSuccess: setResetInfo })
  const isManager = atLeast(user, 'manager')

  return (
    <>
      <h1>{tr("People")}</h1>
      <p className="muted">{isAdmin ? tr("Invite colleagues and manage roles.") : tr("Your colleagues.")}</p>

      {isAdmin && <InvitePanel />}
      {isAdmin && <AddPersonPanel />}

      <section className="card stack">
        <h2>{tr("Directory")}</h2>
        {isAdmin && (
          <div className="row">
            <label className="inline"><input type="checkbox" checked={showDeactivated} onChange={(e) => setShowDeactivated(e.target.checked)} /> Show deactivated</label>
            <label className="inline" data-tip={tr("Include accounts used by integrations, which are hidden by default")}><input type="checkbox" checked={showServiceAccounts} onChange={(e) => setShowServiceAccounts(e.target.checked)} /> {tr("Show service accounts")}</label>
          </div>
        )}
        {patch.isError && <p className="bad">{(patch.error as ApiError).message}</p>}
        <table className="table">
          <thead><tr><th>{tr("Name")}</th><th>{tr("Role")}</th><th>{tr("Manager")}</th><th className="num">{tr("Hours/wk")}</th>{isManager && <th className="num">{tr("Cost rate")}</th>}<th>{tr("Timezone")}</th><th>{tr("GitHub")}</th><th>{tr("Last sign-in")}</th>{(isAdmin || isManager) && <th></th>}</tr></thead>
          <tbody>
            {visible.map((u) => (
              <tr key={u.id} className={u.deactivatedAt ? 'muted' : ''}>
                <td><strong>{u.displayName}</strong>{u.serviceAccount && <span className="tag" data-tip={tr("An account for an integration, left out of payroll, timesheets and scheduled reports")}>{tr("service account")}</span>}<br /><span className="small muted">{u.email}{u.jobTitle ? ` · ${u.jobTitle}` : ''}</span></td>
                <td>
                  {isAdmin && u.id !== user?.id ? (
                    <select value={u.role} onChange={(e) => patch.mutate({ id: u.id, body: { role: e.target.value } })}>
                      {ROLES.map((r) => <option key={r} value={r}>{r}</option>)}
                    </select>
                  ) : u.role}
                </td>
                <td>
                  {isAdmin ? (
                    <select value={u.managerId ?? ''} onChange={(e) => patch.mutate({ id: u.id, body: { managerId: e.target.value || null } })}>
                      <option value="">—</option>
                      {all.filter((m) => m.id !== u.id && !m.deactivatedAt).map((m) => <option key={m.id} value={m.id}>{m.displayName}</option>)}
                    </select>
                  ) : (u.managerId ? byId.get(u.managerId)?.displayName ?? '—' : '—')}
                </td>
                <td className="num">{isAdmin
                  ? <input type="number" min="0" max="168" step="0.5" defaultValue={u.weeklyCapacityHours} style={{ width: '4.5rem' }} onBlur={(e) => { const n = Number(e.target.value); if (n !== u.weeklyCapacityHours) patch.mutate({ id: u.id, body: { weeklyCapacityHours: n } }) }} />
                  : u.weeklyCapacityHours}</td>
                {isManager && <td className="num"><CostRateCell userId={u.id} onEdit={() => setRateUser(u)} /></td>}
                <td className="small">{u.timezone}</td>
                <td className="small">{isAdmin
                  ? <input defaultValue={u.githubLogin ?? ''} placeholder="octocat" maxLength={39} style={{ width: '8rem' }} onBlur={(e) => { const v = e.target.value.trim() || null; if (v !== u.githubLogin) patch.mutate({ id: u.id, body: { githubLogin: v } }) }} />
                  : (u.githubLogin ?? '—')}</td>
                <td className="small">{fmt(u.lastLoginAt)}</td>
                {(isAdmin || isManager) && (
                  <td>
                    <RowMenu label={tr("Actions")} items={[
                      ...(isAdmin ? [{ label: tr("Edit"), tip: tr("Change any field on this person's record, including their email"), onClick: () => setEditUser(u) }] : []),
                      { label: 'Cost rate', tip: 'View or set what this person costs per hour (managers see, admins edit)', onClick: () => setRateUser(u) },
                      ...(isAdmin ? [
                        { label: tr("API tokens"), tip: tr("Issue or revoke tokens that act as this person through the API"), onClick: () => setTokenUser(u) },
                        { label: 'Network restriction', tip: 'Limit sign-in to up to five IP addresses / ranges', onClick: () => setIpUser(u) },
                        { label: 'Unlock sign-in', tip: 'Clear a lockout caused by too many wrong passwords', onClick: () => unlock.mutate(u.id) },
                        { label: 'Password reset link', tip: 'Issue a one-time link (2 days) so they can set a new password', onClick: () => { if (confirm(`Issue a one-time password reset link for ${u.displayName}? It is shown once and expires in 2 days.`)) resetLink.mutate(u) } },
                        ...(u.id !== user?.id ? [u.deactivatedAt
                          ? { label: 'Reactivate', tip: 'Let this person sign in again', onClick: () => patch.mutate({ id: u.id, body: { deactivated: false } }) }
                          : { label: 'Deactivate', tip: 'Sign them out everywhere and block sign-in; history is kept', danger: true, onClick: () => { if (confirm(`Deactivate ${u.displayName}? They will be signed out immediately; their history is kept.`)) patch.mutate({ id: u.id, body: { deactivated: true } }) } }] : []),
                      ] : []),
                    ]} />
                  </td>
                )}
              </tr>
            ))}
          </tbody>
        </table>
      </section>
      {resetInfo && (
        <section className="card stack notice">
          <p><strong>Password reset link for {resetInfo.email}</strong> — shown once, expires {new Date(resetInfo.expiresAt).toLocaleString()}. Send it to them; they choose a new password and their other sessions are signed out.</p>
          <code className="wrap">{resetInfo.url}</code>
          <div className="row"><CopyButton text={resetInfo.url} label={tr("Copy link")} tip="Copy the reset link to send to them" /><button className="link" onClick={() => setResetInfo(null)}>Close</button></div>
        </section>
      )}
      {editUser && <PersonEditor key={editUser.id} user={editUser} colleagues={all} onClose={() => setEditUser(null)} />}
      {tokenUser && <TokensEditor key={tokenUser.id} user={tokenUser} onClose={() => setTokenUser(null)} />}
      {ipUser && <IpAllowlistEditor user={ipUser} onClose={() => setIpUser(null)} />}
      {rateUser && <CostRatesEditor user={rateUser} onClose={() => setRateUser(null)} />}
    </>
  )
}

function InvitePanel() {
  const qc = useQueryClient()
  const [email, setEmail] = useState('')
  const [role, setRole] = useState<Role>('member')
  const [result, setResult] = useState<{ email: string; acceptUrl: string; emailed: boolean } | null>(null)
  const [error, setError] = useState<string | null>(null)
  const invites = useQuery({ queryKey: ['invites'], queryFn: () => api<{ invites: Invite[] }>('/auth/invites') })
  const create = useMutation({
    mutationFn: () => api<{ invite: Invite; token: string; acceptUrl: string; emailed: boolean }>('/auth/invites', { method: 'POST', json: { email, role } }),
    onSuccess: (r) => { setResult({ email: r.invite.email, acceptUrl: r.acceptUrl, emailed: r.emailed }); setEmail(''); void qc.invalidateQueries({ queryKey: ['invites'] }) },
    onError: (e) => setError(e instanceof ApiError ? e.message : 'Could not create invite'),
  })
  const revoke = useMutation({
    mutationFn: (id: string) => api(`/auth/invites/${id}`, { method: 'DELETE' }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['invites'] }),
  })
  const submit = (e: FormEvent) => { e.preventDefault(); setError(null); setResult(null); create.mutate() }
  const pending = (invites.data?.invites ?? []).filter((i) => !i.acceptedAt && new Date(i.expiresAt) > new Date())

  return (
    <section className="card stack">
      <h2>{tr("Invite")}</h2>
      <form onSubmit={submit} className="row">
        <input type="email" placeholder="colleague@adkom.com" required value={email} onChange={(e) => setEmail(e.target.value)} />
        <select value={role} onChange={(e) => setRole(e.target.value as Role)}>{ROLES.map((r) => <option key={r} value={r}>{r}</option>)}</select>
        <button className="button" disabled={create.isPending} data-tip={tr("Create a one-time invite link (7 days) and email it if mail is configured")}>{tr("Create invite")}</button>
      </form>
      {error && <p className="bad">{error}</p>}
      {result && (
        <div className="notice">
          <p>{tr("Invite for")} <strong>{result.email}</strong> is ready.{result.emailed ? ' An email with the link has been sent.' : ''} {result.emailed ? tr("You can also send them the link directly:") : tr("Send them this link — it is shown once and expires in 7 days:")}</p>
          <code className="wrap">{result.acceptUrl}</code>
          <div><CopyButton text={result.acceptUrl} label={tr("Copy link")} tip="Copy the invite link to send to them" /></div>
        </div>
      )}
      {pending.length > 0 && (
        <table className="table">
          <thead><tr><th>{tr("Pending")}</th><th>{tr("Role")}</th><th>{tr("Expires")}</th><th></th></tr></thead>
          <tbody>
            {pending.map((i) => (
              <tr key={i.id}><td>{i.email}</td><td>{i.role}</td><td className="small">{fmt(i.expiresAt)}</td>
                <td><button className="link danger" data-tip={tr("Cancel this pending invite")} onClick={() => revoke.mutate(i.id)}>Revoke</button></td></tr>
            ))}
          </tbody>
        </table>
      )}
    </section>
  )
}

/**
 * APM-9: create an account outright, with no invite and no email. The other
 * half of "add someone" — sits under the invite form because the two are
 * alternatives, not steps.
 */
function AddPersonPanel() {
  const qc = useQueryClient()
  const [email, setEmail] = useState('')
  const [displayName, setDisplayName] = useState('')
  const [timezone, setTimezone] = useState(browserTimezone())
  const [role, setRole] = useState<Role>('member')
  const [serviceAccount, setServiceAccount] = useState(false)
  const [pwMode, setPwMode] = useState<'none' | 'generate' | 'set'>('none')
  const [password, setPassword] = useState('')
  const [error, setError] = useState<string | null>(null)
  const [created, setCreated] = useState<{ displayName: string; email: string; password: string | null; canSignIn: boolean } | null>(null)
  const create = useMutation({
    mutationFn: () => api<{ user: DirectoryUser; password: string | null }>('/users', {
      method: 'POST',
      json: {
        email, displayName, timezone, role, serviceAccount,
        ...(pwMode === 'set' ? { password } : {}),
        ...(pwMode === 'generate' ? { generatePassword: true } : {}),
      },
    }),
    onSuccess: (r) => {
      setCreated({ displayName: r.user.displayName, email: r.user.email, password: r.password, canSignIn: pwMode !== 'none' })
      setEmail(''); setDisplayName(''); setPassword(''); setPwMode('none'); setServiceAccount(false); setRole('member')
      void qc.invalidateQueries({ queryKey: ['users'] })
    },
    onError: (e) => setError(e instanceof ApiError ? e.message : 'Could not create the account'),
  })
  const submit = (e: FormEvent) => { e.preventDefault(); setError(null); setCreated(null); create.mutate() }

  return (
    <section className="card stack">
      <h2>{tr("Add a person")}</h2>
      <p className="small muted">{tr("An invite lets them pick their own password. Creating an account here sends no email at all: it exists straight away, and you decide whether it has a password.")}</p>
      <form onSubmit={submit} className="stack">
        <label>{tr("Email")}<input type="email" required value={email} onChange={(e) => setEmail(e.target.value)} placeholder="colleague@adkom.com" maxLength={320} /></label>
        <label>{tr("Name")}<input required value={displayName} onChange={(e) => setDisplayName(e.target.value)} maxLength={200} placeholder="Ada Lovelace" /></label>
        <label>{tr("Timezone")}<input required value={timezone} onChange={(e) => setTimezone(e.target.value)} placeholder="America/Denver" list="tz-suggestions" maxLength={64} />
          <datalist id="tz-suggestions">{COMMON_ZONES.map((z) => <option key={z} value={z} />)}</datalist>
          <span className="hint">{tr("Shows their day in the right hours; it never changes which week an entry belongs to.")}</span></label>
        <label>{tr("Role")}<select value={role} onChange={(e) => setRole(e.target.value as Role)}>{ROLES.map((r) => <option key={r} value={r}>{r}</option>)}</select></label>
        <div>
          <label className="inline"><input type="checkbox" checked={serviceAccount} onChange={(e) => setServiceAccount(e.target.checked)} /> {tr("Service account")}</label>
          <span className="hint">{tr("For an integration such as Claude Code rather than a colleague: it stays out of payroll, timesheets and scheduled reports.")}</span>
        </div>
        <label>{tr("Password")}
          <select value={pwMode} onChange={(e) => { setPwMode(e.target.value as 'none' | 'generate' | 'set'); setPassword('') }} data-tip={tr("Decides whether this account can sign in the moment you create it")}>
            <option value="none">{tr("none — they cannot sign in yet")}</option>
            <option value="generate">{tr("generate one and show it to me once")}</option>
            <option value="set">{tr("set one now")}</option>
          </select>
          {pwMode === 'none' && <span className="hint">{tr("Send them a password reset link from the directory when they are ready to sign in.")}</span>}
        </label>
        {pwMode === 'set' && (
          <label>{tr("New password")}<input type="password" autoComplete="new-password" required minLength={12} maxLength={256} value={password} onChange={(e) => setPassword(e.target.value)} />
            <span className="hint">{tr("At least 12 characters. A password found in a public breach is refused.")}</span></label>
        )}
        <div><button className="button primary" disabled={create.isPending} data-tip={tr("Create the account now — nobody is emailed")}>{tr("Create account")}</button></div>
      </form>
      {error && <p className="bad">{error}</p>}
      {created && (
        <div className="notice stack" role="status">
          <p><strong>{created.displayName}</strong> ({created.email}) {tr("now has an account.")}</p>
          {created.password ? (
            <>
              <p className="small">{tr("This password is shown once and will not be shown again. Give it to them over a channel you trust; they can change it under Account.")}</p>
              <div className="row"><code className="wrap">{created.password}</code><CopyButton text={created.password} label={tr("Copy password")} tip={tr("Copy the password so you can pass it on")} className="link" /></div>
            </>
          ) : (
            <p className="small muted">{created.canSignIn ? tr("They can sign in with the password you set.") : tr("There is no password yet, so nobody can sign in as them. Issue a password reset link from the directory when they are ready.")}</p>
          )}
          <div><button className="link small" onClick={() => setCreated(null)}>{tr("Dismiss")}</button></div>
        </div>
      )}
    </section>
  )
}

/**
 * APM-9: the whole of a person's record in one admin panel — everything the
 * directory shows inline, plus the fields it only displays.
 */
function PersonEditor({ user, colleagues, onClose }: { user: DirectoryUser; colleagues: DirectoryUser[]; onClose: () => void }) {
  const qc = useQueryClient()
  const [displayName, setDisplayName] = useState(user.displayName)
  const [email, setEmail] = useState(user.email)
  const [jobTitle, setJobTitle] = useState(user.jobTitle ?? '')
  const [timezone, setTimezone] = useState(user.timezone)
  const [locale, setLocale] = useState(user.locale)
  const [role, setRole] = useState<Role>(user.role)
  const [managerId, setManagerId] = useState(user.managerId ?? '')
  const [capacity, setCapacity] = useState(String(user.weeklyCapacityHours))
  const [workingDays, setWorkingDays] = useState(user.workingDaysPerWeek === null ? '' : String(user.workingDaysPerWeek))
  const [avatarUrl, setAvatarUrl] = useState(user.avatarUrl ?? '')
  const [githubLogin, setGithubLogin] = useState(user.githubLogin ?? '')
  const [serviceAccount, setServiceAccount] = useState(user.serviceAccount)
  const [err, setErr] = useState<string | null>(null)
  const [saved, setSaved] = useState(false)
  const save = useMutation({
    mutationFn: (body: Record<string, unknown>) => api<{ user: DirectoryUser }>(`/users/${user.id}`, { method: 'PATCH', json: body }),
    onSuccess: () => { setSaved(true); void qc.invalidateQueries({ queryKey: ['users'] }) },
    onError: (e) => setErr(e instanceof ApiError ? e.message : 'Could not save'),
  })

  const submit = (e: FormEvent) => {
    e.preventDefault(); setErr(null); setSaved(false)
    const body: Record<string, unknown> = {}
    if (displayName !== user.displayName) body.displayName = displayName
    const mail = email.trim().toLowerCase()
    if (mail !== user.email.toLowerCase()) {
      // Password resets follow the address, so never change it by accident.
      if (!confirm(tr("Change the email for {name} to {email}? That address is where password resets go, and they are told about the change.", { name: user.displayName, email: mail }))) return
      body.email = mail
    }
    const job = jobTitle.trim() || null
    if (job !== user.jobTitle) body.jobTitle = job
    if (timezone !== user.timezone) body.timezone = timezone
    if (locale !== user.locale) body.locale = locale
    if (role !== user.role) body.role = role
    if ((managerId || null) !== user.managerId) body.managerId = managerId || null
    const hours = Number(capacity)
    if (!Number.isNaN(hours) && hours !== user.weeklyCapacityHours) body.weeklyCapacityHours = hours
    const days = workingDays.trim() === '' ? null : Number(workingDays)
    if (days !== user.workingDaysPerWeek && (days === null || Number.isInteger(days))) body.workingDaysPerWeek = days
    const avatar = avatarUrl.trim() || null
    if (avatar !== user.avatarUrl) body.avatarUrl = avatar
    const gh = githubLogin.trim() || null
    if (gh !== user.githubLogin) body.githubLogin = gh
    if (serviceAccount !== user.serviceAccount) body.serviceAccount = serviceAccount
    if (Object.keys(body).length === 0) { setSaved(true); return }
    save.mutate(body)
  }

  return (
    <section className="card stack editor">
      <div className="row between"><h2 style={{ margin: 0 }}>{tr("Edit person")} — {user.displayName}</h2><button className="link" onClick={onClose}>{tr("Close")}</button></div>
      <p className="small muted">{tr("Everything on this person's record. Changes are audit-logged against your name.")}</p>
      <form onSubmit={submit} className="stack">
        <label>{tr("Name")}<input required maxLength={200} value={displayName} onChange={(e) => setDisplayName(e.target.value)} /></label>
        <label>{tr("Email")}<input type="email" required maxLength={320} value={email} onChange={(e) => setEmail(e.target.value)} />
          <span className="hint">{tr("Where password resets go. Change it and they are emailed about it.")}</span></label>
        <label>{tr("Job title")}<input maxLength={200} value={jobTitle} onChange={(e) => setJobTitle(e.target.value)} /></label>
        <label>{tr("Timezone")}<input required maxLength={64} value={timezone} onChange={(e) => setTimezone(e.target.value)} placeholder="America/Denver" list="tz-suggestions" />
          <datalist id="tz-suggestions">{COMMON_ZONES.map((z) => <option key={z} value={z} />)}</datalist></label>
        <label>{tr("Language")}<select value={locale} onChange={(e) => setLocale(e.target.value)}>{LOCALES.map((l) => <option key={l.code} value={l.code}>{l.name}</option>)}</select>
          <span className="hint">{tr("What they see the app in until they choose otherwise.")}</span></label>
        <label>{tr("Role")}<select value={role} onChange={(e) => setRole(e.target.value as Role)}>{ROLES.map((r) => <option key={r} value={r}>{r}</option>)}</select></label>
        <label>{tr("Manager")}<select value={managerId} onChange={(e) => setManagerId(e.target.value)}>
          <option value="">—</option>
          {colleagues.filter((m) => m.id !== user.id && !m.deactivatedAt).map((m) => <option key={m.id} value={m.id}>{m.displayName}</option>)}
        </select></label>
        <label>{tr("Hours per week")}<input type="number" min="0" max="168" step="0.5" value={capacity} onChange={(e) => setCapacity(e.target.value)} style={{ width: '8rem' }} />
          <span className="hint">{tr("How much of their time the schedule may plan for.")}</span></label>
        <label>{tr("Working days per week")}<input type="number" min="1" max="7" step="1" value={workingDays} onChange={(e) => setWorkingDays(e.target.value)} style={{ width: '8rem' }} placeholder="5" />
          <span className="hint">{tr("Leave it empty to follow the organisation's working week.")}</span></label>
        <label>{tr("Avatar URL")}<input type="url" maxLength={2000} value={avatarUrl} onChange={(e) => setAvatarUrl(e.target.value)} placeholder="https://…" /></label>
        <label>{tr("GitHub username")}<input maxLength={39} value={githubLogin} onChange={(e) => setGithubLogin(e.target.value)} placeholder="octocat" />
          <span className="hint">{tr("Issues assigned to this GitHub user become tasks assigned to them.")}</span></label>
        <div>
          <label className="inline"><input type="checkbox" checked={serviceAccount} onChange={(e) => setServiceAccount(e.target.checked)} /> {tr("Service account")}</label>
          <span className="hint">{tr("Keeps this account out of payroll, timesheets and scheduled reports.")}</span>
        </div>
        <div className="row"><button className="button primary" disabled={save.isPending} data-tip={tr("Save every change on this record")}>{tr("Save")}</button><button type="button" className="button" onClick={onClose}>{tr("Cancel")}</button></div>
      </form>
      {err && <p className="bad">{err}</p>}
      {saved && !err && <p className="good">{tr("Saved.")}</p>}
    </section>
  )
}

/**
 * APM-9: API tokens for somebody else. This is how a persona — a Claude Code
 * account of its own — gets a credential without anyone learning its password.
 */
function TokensEditor({ user, onClose }: { user: DirectoryUser; onClose: () => void }) {
  const qc = useQueryClient()
  const q = useQuery({ queryKey: ['api-tokens', user.id], queryFn: () => api<{ tokens: ApiToken[]; mcpUrl: string }>(`/auth/tokens?userId=${user.id}`) })
  const [name, setName] = useState('')
  const [scope, setScope] = useState<'read' | 'write'>('write')
  const [expires, setExpires] = useState('')
  const [fresh, setFresh] = useState<{ token: string; name: string } | null>(null)
  const [err, setErr] = useState<string | null>(null)
  const onError = (e: unknown) => setErr(e instanceof ApiError ? e.message : 'Request failed')
  const create = useMutation({
    mutationFn: () => api<{ token: string; info: ApiToken }>('/auth/tokens', { method: 'POST', json: { name, scope, expiresInDays: expires ? Number(expires) : null, userId: user.id } }),
    onSuccess: (r) => { setFresh({ token: r.token, name: r.info.name }); setName(''); void qc.invalidateQueries({ queryKey: ['api-tokens', user.id] }) }, onError,
  })
  const revoke = useMutation({
    mutationFn: (id: string) => api(`/auth/tokens/${id}`, { method: 'DELETE' }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['api-tokens', user.id] }), onError,
  })
  const mcpUrl = q.data?.mcpUrl ?? ''
  const exportLine = `export APM_TOKEN=${fresh?.token ?? 'apm_…'}`
  const addCmd = `claude mcp add --transport http --scope user apm ${mcpUrl} --header "Authorization: Bearer \${APM_TOKEN}"`
  const live = (q.data?.tokens ?? []).filter((t) => !t.revokedAt)
  const revoked = (q.data?.tokens ?? []).filter((t) => t.revokedAt)

  return (
    <section className="card stack editor">
      <div className="row between"><h2 style={{ margin: 0 }}>{tr("API tokens")} — {user.displayName}</h2><button className="link" onClick={onClose}>{tr("Close")}</button></div>
      <p className="small muted">{tr("A token acts as this person through the API: projects, portfolios, tasks, schedules and their time, never rates, payroll or admin. Everything it does is audited as them, marked with the token's name, and your name is recorded as the one who issued it.")}</p>
      {fresh && (
        <div className="notice stack" role="status">
          <p><strong>{tr("Token")} "{fresh.name}" {tr("created. Copy it now — it will not be shown again.")}</strong></p>
          <div className="row"><code className="wrap">{fresh.token}</code><CopyButton text={fresh.token} tip={tr("Copy the token")} className="link" /></div>
          {user.serviceAccount && (
            <>
              <p className="small muted">{tr("To connect Claude Code as this account, put the token in the shell and add the server once:")}</p>
              <div className="row"><code className="wrap">{exportLine}</code><CopyButton text={exportLine} tip={tr("Copy the export line")} className="link" /></div>
              <div className="row"><code className="wrap">{addCmd}</code><CopyButton text={addCmd} tip={tr("Copy the claude mcp add command")} className="link" /></div>
            </>
          )}
          <div><button className="link small" onClick={() => setFresh(null)}>{tr("Dismiss")}</button></div>
        </div>
      )}
      {live.length > 0 ? (
        <table className="table">
          <thead><tr><th>{tr("Name")}</th><th>{tr("Scope")}</th><th>{tr("Created")}</th><th>{tr("Last used")}</th><th>{tr("Expires")}</th><th></th></tr></thead>
          <tbody>
            {live.map((t) => (
              <tr key={t.id}>
                <td>{t.name}</td>
                <td><span className={`tag ${t.scope === 'write' ? 'prio-high' : ''}`}>{t.scope}</span></td>
                <td className="small">{fmt(t.createdAt)}</td>
                <td className="small">{fmt(t.lastUsedAt)}</td>
                <td className="small">{t.expiresAt ? fmt(t.expiresAt) : tr("never")}</td>
                <td><button className="link danger" data-tip={tr("Stop anything using this token, immediately")} onClick={() => { if (confirm(tr("Revoke the token \"{name}\"? Anything using it stops working immediately.", { name: t.name }))) revoke.mutate(t.id) }}>{tr("Revoke")}</button></td>
              </tr>
            ))}
          </tbody>
        </table>
      ) : <p className="muted small">{tr("No tokens yet.")}</p>}
      {revoked.length > 0 && <p className="small muted">{tr("Revoked:")} {revoked.map((t) => `${t.name} (${fmt(t.revokedAt)})`).join(' · ')}</p>}
      <form className="row" onSubmit={(e: FormEvent) => { e.preventDefault(); setErr(null); if (name.trim()) create.mutate() }}>
        <input placeholder={tr("Token name, e.g. Claude Code")} value={name} onChange={(e) => setName(e.target.value)} required maxLength={100} style={{ flex: 1 }} />
        <select value={scope} onChange={(e) => setScope(e.target.value as 'read' | 'write')} data-tip={tr("Read: look only. Write: create and change tasks, links, milestones and time")}>
          <option value="write">{tr("read and write")}</option>
          <option value="read">{tr("read only")}</option>
        </select>
        <select value={expires} onChange={(e) => setExpires(e.target.value)} data-tip={tr("When the token stops working on its own")}>
          <option value="">{tr("never expires")}</option>
          <option value="30">{tr("30 days")}</option>
          <option value="90">{tr("90 days")}</option>
          <option value="365">{tr("1 year")}</option>
        </select>
        <button className="button primary" disabled={create.isPending} data-tip={tr("Issue a token for this person and show it once")}>{tr("Create token")}</button>
      </form>
      {err && <p className="bad">{err}</p>}
    </section>
  )
}

/** Current cost rate for a person (managers/admins); click to open the editor. */
function CostRateCell({ userId, onEdit }: { userId: string; onEdit: () => void }) {
  const q = useQuery({ queryKey: ['cost-rates', userId], queryFn: () => api<{ rates: { amount: number; currency: string; current: boolean }[] }>(`/users/${userId}/cost-rates`), staleTime: 60_000 })
  const cur = q.data?.rates.find((r) => r.current)
  return <button className="link" data-tip={tr("What this person costs per hour (effective-dated); click to view history or change it")} onClick={onEdit}>{cur ? `${cur.amount.toFixed(2)} ${cur.currency}/h` : 'set…'}</button>
}
